import gemIcon from '~/assets/gem.svg'
import leafIcon from '~/assets/leaf.svg'

import { cn } from '@forest/shared'
import * as Dialog from '@kobalte/core/dialog'
import { useMutation } from '@tanstack/solid-query'
import { DragGesture } from '@use-gesture/vanilla'
import isNetworkError from 'is-network-error'
import { isHTTPError, isTimeoutError } from 'ky'
import { createEffect, createSignal, Index, onCleanup, onMount } from 'solid-js'
import { CurrencyBar } from '~/components/currency-bar'
import { NoteIcon } from '~/components/icon/note'
import { getServiceClient } from '~/lib/api'
import { m } from '~/paraglide/messages'
import { invalidateBalance } from '~/resources/currency'
import { showDialog } from '~/store/dialog'
import { DonationCard } from './card'
import { configs } from './config'

export function DonationCards(props: {
  onDonateSuccess: (plan: number) => void
}) {
  const [isOpen, setIsOpen] = createSignal(false)
  const [centeredCardIdx, setCenteredCardIdx] = createSignal(0)

  const donateMutation = useMutation(() => ({
    mutationFn: async (index: number) => {
      const { client, userId } = await getServiceClient()
      await client.post(`store/users/${userId}/purchase`, {
        json: {
          product_id: configs[index].productId,
          quantity: 1,
        },
      })
      return index
    },
    onSuccess: (index) => {
      setIsOpen(false)
      invalidateBalance()
      props.onDonateSuccess(index + 1)
    },
    onError: (error) => {
      setTimeout(() => {
        if (
          isTimeoutError(error) ||
          (isNetworkError as (err: unknown) => boolean)(error)
        ) {
          showDialog({
            title: m.dialog_purchase_fail_title(),
            description: m.dialog_data_load_fail_description(),
            primaryButtonText: m.ok(),
          })
          return
        }
        if (isHTTPError(error)) {
          if (error.response.status === 422) {
            showDialog({
              title: m.dialog_not_enough_balance_gem_title(),
              description: m.dialog_not_enough_balance_gem_description(),
              primaryButtonText: m.ok(),
            })
          } else {
            showDialog({
              title: m.dialog_purchase_fail_title(),
              description: m.error_code_general({
                status: error.response.status,
              }),
              primaryButtonText: m.ok(),
            })
          }
          return
        }
        showDialog({
          title: m.dialog_purchase_fail_title(),
          description: m.error_code_general({
            status: error.message,
          }),
          primaryButtonText: m.ok(),
        })
      })
    },
  }))

  const handleDonate = (idx: number) => {
    showDialog({
      title: m.dialog_donate_confirm_title(),
      description: m.dialog_donate_confirm_gem_description({
        gem_number: String(configs[idx].price),
      }),
      primaryButtonText: m.confirm_btn(),
      secondaryButtonText: m.cancel_btn(),
      asyncPrimaryAction: async () => {
        await donateMutation.mutateAsync(idx)
      },
    })
  }

  createEffect(() => {
    if (!isOpen()) {
      setTimeout(() => {
        setCenteredCardIdx(0)
      }, 500)
    }
  })

  return (
    <Dialog.Dialog open={isOpen()} onOpenChange={setIsOpen}>
      <Dialog.Trigger as="div">
        <button class="group peer relative w-full rounded-xl bg-gradient-to-r from-[#009596] to-[#007F57] p-[2px] transition-transform active:translate-y-[6px]">
          <div class="flex h-[46px] items-center justify-center space-x-[6px] rounded-xl bg-gradient-to-r from-[#2AF4FF] to-[#18FE95]">
            <span class="text-[18px] font-bold text-[#013E45]">
              {m.earth_day_2025_donate_btn()}
            </span>
            <img src={leafIcon} alt="" class="size-[21px]" />
          </div>
          <div class="h-[6px] max-h-[6px] transition-all group-active:max-h-0" />
        </button>
        <div class="h-[6px] max-h-0 transition-all peer-active:max-h-[6px]" />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content
          class="fixed inset-0 z-50 backdrop-blur data-[expanded]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[expanded]:fade-in-0"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          style={{
            background:
              'linear-gradient(180deg, rgba(0, 146, 143, 0.70) 0%, rgba(9, 21, 36, 0.70) 100%)',
          }}
        >
          <button
            class="absolute left-4 top-[calc(16px+var(--safe-area-top))] flex size-[42px] items-center justify-center"
            onClick={() => setIsOpen(false)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L11 11M1 11L11 1"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="absolute right-4 top-[calc(16px+var(--safe-area-top))]">
            <CurrencyBar />
          </div>

          <Cards
            class="mt-[calc(50vh-270px+var(--safe-area-top))]"
            centeredCardIdx={centeredCardIdx()}
            setCenteredCardIdx={setCenteredCardIdx}
            onDonate={handleDonate}
          />

          <div class="absolute bottom-12 left-8 right-8 z-50">
            <div class="flex w-full justify-center sm:hidden">
              <DonateButton
                value={configs[centeredCardIdx()].price}
                onClick={() => handleDonate(centeredCardIdx())}
              />
            </div>
            <p class="mt-[10px] flex w-full items-center justify-center space-x-[3px] text-[#01FDE9] sm:mt-0">
              <span class="inline-block w-[14px]">
                <NoteIcon />
              </span>
              <span>{m.earth_day_2025_donate_plan_note()}</span>
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Dialog>
  )
}

function Cards(props: {
  class?: string
  centeredCardIdx: number
  setCenteredCardIdx: (idx: number) => void
  onDonate: (index: number) => void
}) {
  let trackRef!: HTMLDivElement
  let dragGesture: any = null

  function getCenteredCardIdx(): number {
    const cards = trackRef.querySelectorAll('.donation-card-snap')
    if (!cards.length) return 0
    const trackRect = trackRef.getBoundingClientRect()
    const center = trackRef.scrollLeft + trackRect.width / 2
    let minDist = Infinity
    let idx = 0
    cards.forEach((card, i) => {
      const el = card as HTMLElement
      const cardRect = el.getBoundingClientRect()
      const cardCenter = el.offsetLeft + cardRect.width / 2
      const dist = Math.abs(cardCenter - center)
      if (dist < minDist) {
        minDist = dist
        idx = i
      }
    })
    return idx
  }

  function snapToCard(idx: number) {
    const cards = trackRef.querySelectorAll('.donation-card-snap')
    if (!cards.length) return
    const targetCard = cards[idx] as HTMLElement
    const trackRect = trackRef.getBoundingClientRect()
    const cardRect = targetCard.getBoundingClientRect()
    const snapTo =
      targetCard.offsetLeft - (trackRect.width - cardRect.width) / 2
    trackRef.scrollTo({ left: snapTo, behavior: 'smooth' })
    props.setCenteredCardIdx(idx)
  }

  function getCardCount() {
    return trackRef.querySelectorAll('.donation-card-snap').length
  }

  onMount(() => {
    let startScroll = 0
    let startIdx = 0
    let lastIdx = 0
    dragGesture = new DragGesture(
      trackRef,
      ({ active, movement: [mx], swipe: [swipeX], event }) => {
        const cardCount = getCardCount()
        if (active) {
          // On drag start, record initial scroll position and card index
          if (event.type === 'pointerdown' || event.type === 'touchstart') {
            startScroll = trackRef.scrollLeft
            startIdx = getCenteredCardIdx()
            lastIdx = startIdx
          }
          // Move the track horizontally
          trackRef.scrollLeft = startScroll - mx
        } else {
          // On drag end, use swipe property for card snapping
          let targetIdx = getCenteredCardIdx()
          if (swipeX !== 0) {
            // Swipe detected: -1 (left), 1 (right)
            if (swipeX === -1 && lastIdx < cardCount - 1) {
              targetIdx = lastIdx + 1
            } else if (swipeX === 1 && lastIdx > 0) {
              targetIdx = lastIdx - 1
            }
          } else {
            // No swipe detected, snap to the closest card (not always lastIdx)
            targetIdx = getCenteredCardIdx()
          }
          snapToCard(targetIdx)
        }
      },
    )
  })
  onCleanup(() => {
    if (dragGesture) dragGesture.destroy()
  })

  return (
    <div
      class={cn(
        'no-scrollbar donation-card-track flex w-full space-x-8 overflow-x-hidden pt-[90px]',
        props.class,
      )}
      ref={trackRef}
      style="touch-action: pan-y; user-select: none;"
    >
      <div class="w-[calc(50vw-136.5px-32px)] shrink-0" />
      <Index each={Array.from({ length: 4 })}>
        {(_, index) => (
          <div
            class="donation-card-snap flex flex-col"
            style="scroll-snap-align: center;"
          >
            <DonationCard index={index} />
            <div class="mt-9 hidden sm:block">
              <DonateButton
                value={configs[index].price}
                onClick={() => props.onDonate(index)}
              />
            </div>
          </div>
        )}
      </Index>
      <div class="w-[calc(50vw-136.5px-32px)] shrink-0" />
    </div>
  )
}

function DonateButton(props: { onClick: () => void; value: number }) {
  return (
    <div class="mx-auto w-full max-w-[640px]">
      <button
        class="group peer relative w-full rounded-xl bg-gradient-to-r from-[#A8D113] to-[#3A9A03] p-[2px] transition-transform active:translate-y-[6px]"
        onClick={props.onClick}
      >
        <div class="flex h-[46px] items-center justify-center space-x-[6px] rounded-xl bg-gradient-to-r from-[#E5FF3A] to-[#60FF26]">
          <span class="text-[18px] font-bold text-[#013E45]">
            {props.value}
          </span>
          <img class="size-6" src={gemIcon} alt="" />
        </div>
        <div class="h-[6px] max-h-[6px] transition-all group-active:max-h-0" />
      </button>
      <div class="h-[6px] max-h-0 transition-all peer-active:max-h-[6px]" />
    </div>
  )
}
